import { Landmark as CardIconLandmark } from 'lucide-react';
import { CardHeader as ContentCardHeader } from '@hue/design-system';
import {
  Button,
  CurrencyInput,
  Input,
  Modal,
  Select,
  Tooltip,
  formatCurrency,
} from '@hue/design-system';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Info, SlidersHorizontal } from 'lucide-react';
import { useState } from 'react';
import {
  getApiError,
  investmentMetricsApi,
  type Assumptions,
  type Metric,
  type MetricsBase,
  type MetricsView,
} from '../../lib/api.js';
import { EmptyCard, ErrorCard, LoadingCard, RequestAlert } from '../../app/data-view.js';
import { formatMetric, formatScaled, parseScaled } from './metrics-format.js';
import { PageHeader } from '../../app/page-title.js';

export interface MetricsCapabilities {
  canManageAssumptions: boolean;
}

interface IndicatorInfo {
  key: string;
  title: string;
  explanation: string;
}

/// O que sai do fluxo projetado: dependem só do investimento, da receita e da despesa.
const FLOW_INDICATORS: IndicatorInfo[] = [
  {
    key: 'roi',
    title: 'ROI',
    explanation:
      'Retorno sobre o investimento no horizonte. Acima de 0% cria valor: 50% quer dizer que cada R$ 1 investido devolve R$ 1,50.',
  },
  {
    key: 'npv',
    title: 'VPL',
    explanation:
      'Valor presente líquido: o que os meses futuros valem hoje, menos o investimento. Acima de zero, o negócio rende mais que a taxa de desconto.',
  },
  {
    key: 'irr',
    title: 'TIR ao ano',
    explanation:
      'A taxa que o próprio projeto rende. Compare com a taxa de desconto: acima dela, vale a pena; abaixo, o dinheiro rende mais em outro lugar.',
  },
  {
    key: 'paybackSimple',
    title: 'Payback',
    explanation:
      'Em quantos meses o lucro acumulado empata com o investimento, sem considerar o valor do tempo.',
  },
  {
    key: 'paybackDiscounted',
    title: 'Payback descontado',
    explanation:
      'O mesmo prazo, trazendo cada mês a valor presente. É o prazo honesto de recuperação e nunca é menor que o simples.',
  },
  {
    key: 'profitabilityIndex',
    title: 'Índice de lucratividade',
    explanation:
      'Valor presente das entradas dividido pelo investimento. Acima de 1 cria valor: 1,30 é R$ 1,30 de valor presente por R$ 1 investido.',
  },
  {
    key: 'moic',
    title: 'MOIC',
    explanation:
      'Quantas vezes o capital se multiplica no horizonte, sem desconto. 2× é dobrar o dinheiro investido.',
  },
];

/// O que depende de números que alguém precisa informar: sem a premissa, o card diz o que falta.
const COMPANY_INDICATORS: IndicatorInfo[] = [
  {
    key: 'roic',
    title: 'ROIC',
    explanation:
      'Retorno sobre o capital investido: lucro dividido pelo capital que sustenta a operação. Compare com o WACC.',
  },
  {
    key: 'roe',
    title: 'ROE',
    explanation: 'Retorno sobre o patrimônio líquido: quanto rende o dinheiro que é dos sócios.',
  },
  {
    key: 'roa',
    title: 'ROA',
    explanation: 'Retorno sobre o ativo: quanto de lucro cada real de ativo gera.',
  },
  {
    key: 'eva',
    title: 'EVA',
    explanation:
      'Valor econômico agregado: o lucro menos o custo do capital. Acima de zero, o negócio pagou o capital e ainda sobrou.',
  },
  {
    key: 'valuation',
    title: 'Valuation',
    explanation: 'Valor estimado do negócio: EBITDA anual multiplicado pelo múltiplo do setor.',
  },
  {
    key: 'cac',
    title: 'CAC',
    explanation: 'Custo de aquisição de cliente: quanto custa trazer um cliente novo.',
  },
  {
    key: 'ltv',
    title: 'LTV',
    explanation:
      'Quanto um cliente deixa de margem enquanto for cliente: ticket × margem × meses de vida.',
  },
  {
    key: 'ltvOverCac',
    title: 'LTV / CAC',
    explanation:
      'Quanto o cliente devolve para cada real gasto para conquistá-lo. Saudável a partir de 3×.',
  },
];

const BASE_CHOICES: Array<{ value: MetricsBase; title: string; explanation: string }> = [
  {
    value: 'ENTRIES',
    title: 'Soma dos lançamentos',
    explanation: 'Quanto custou montar o negócio: tudo que foi comprado e registrado aqui.',
  },
  {
    value: 'CONTRIBUTIONS',
    title: 'Soma dos aportes',
    explanation:
      'Quanto de capital entrou na empresa, inclusive empréstimo — que é dinheiro que entra sem virar compra.',
  },
  {
    value: 'MANUAL',
    title: 'Valor informado',
    explanation:
      'Para quem investiu fora deste módulo: aporte antigo, bem herdado, obra paga por outra empresa.',
  },
];

const BASE_LABELS: Record<MetricsBase, string> = {
  ENTRIES: 'soma dos lançamentos',
  CONTRIBUTIONS: 'soma dos aportes',
  MANUAL: 'valor informado',
};

export function CompanyMetrics({ capabilities }: { capabilities: MetricsCapabilities }) {
  const queryClient = useQueryClient();
  const [assumptionsOpen, setAssumptionsOpen] = useState(false);

  const view = useQuery({
    queryKey: ['company', 'investment', 'metrics'],
    queryFn: investmentMetricsApi.get,
    retry: false,
  });

  /// Falha aqui sobe para quem chamou: o aviso pertence ao lugar onde a pessoa clicou. Um alerta
  /// no corpo da tela ficaria atrás do diálogo aberto, invisível justamente para quem precisa dele.
  async function save(assumptions: Assumptions) {
    const saved = await investmentMetricsApi.saveAssumptions(assumptions);
    queryClient.setQueryData(['company', 'investment', 'metrics'], saved);
    setAssumptionsOpen(false);
  }

  return (
    <section aria-labelledby="company-metrics-title">
      <PageHeader
        trail="Investimento inicial / Indicadores"
        id="company-metrics-title"
        title="Indicadores"
        description="O que os lançamentos e os aportes dizem sobre o retorno do negócio."
        actions={
          <>
            {capabilities.canManageAssumptions && view.data ? (
              <Button variant="outline" onClick={() => setAssumptionsOpen(true)}>
                <SlidersHorizontal className="size-4" />
                Premissas
              </Button>
            ) : null}
          </>
        }
      />

      {view.isLoading ? (
        <LoadingCard label="Carregando indicadores" />
      ) : view.error ? (
        <ErrorCard message={getApiError(view.error)} />
      ) : view.data ? (
        <MetricsBody
          view={view.data}
          capabilities={capabilities}
          onChooseBase={save}
          onOpenAssumptions={() => setAssumptionsOpen(true)}
        />
      ) : null}

      {view.data && assumptionsOpen ? (
        <AssumptionsModal
          onOpenChange={setAssumptionsOpen}
          assumptions={view.data.assumptions}
          onSave={save}
        />
      ) : null}
    </section>
  );
}

function MetricsBody({
  view,
  capabilities,
  onChooseBase,
  onOpenAssumptions,
}: {
  view: MetricsView;
  capabilities: MetricsCapabilities;
  onChooseBase: (assumptions: Assumptions) => Promise<void>;
  onOpenAssumptions: () => void;
}) {
  const { kind, cents: invested } = view.metrics.base;
  if (kind === null || invested === null) {
    return <BaseChooser view={view} capabilities={capabilities} onChoose={onChooseBase} />;
  }

  const { flow } = view.metrics;
  return (
    <>
      <div className="mb-6 rounded-2xl border border-surface-border bg-surface-card p-5 shadow-soft">
        <ContentCardHeader
          title="Base do investimento"
          icon={<CardIconLandmark className="size-4" />}
        />
        <p className="text-sm text-content-secondary">
          Investimento de{' '}
          <strong className="font-semibold text-content">{formatCurrency(invested)}</strong> pela{' '}
          {BASE_LABELS[kind]}
          {flow.monthlyCents === null ? (
            <>, sem fluxo mensal projetado enquanto faltar receita ou despesa.</>
          ) : (
            <>
              , com fluxo mensal projetado de{' '}
              <strong className="font-semibold text-content">
                {formatCurrency(flow.monthlyCents)}
              </strong>{' '}
              por {flow.horizonMonths} meses.
            </>
          )}
        </p>
        {capabilities.canManageAssumptions ? (
          <button
            type="button"
            onClick={onOpenAssumptions}
            className="mt-2 text-xs font-medium text-brand-400 underline-offset-2 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
          >
            Trocar a base ou revisar as premissas
          </button>
        ) : null}
      </div>

      <IndicatorGroup
        title="Retorno do projeto"
        description="Saem do investimento e do fluxo mensal projetado."
        group={FLOW_INDICATORS}
        indicators={view.metrics.indicators}
      />
      <IndicatorGroup
        title="Desempenho da empresa"
        description="Dependem dos números que a empresa informa nas premissas."
        group={COMPANY_INDICATORS}
        indicators={view.metrics.indicators}
      />
    </>
  );
}

/// A primeira visita pergunta em vez de assumir.
///
/// As três bases medem coisas diferentes e nenhuma serve para toda empresa. Mostrar o valor de cada
/// uma ao lado da opção é o que transforma a pergunta em decisão: a pessoa escolhe vendo o número
/// que vai sustentar todos os indicadores da tela.
function BaseChooser({
  view,
  capabilities,
  onChoose,
}: {
  view: MetricsView;
  capabilities: MetricsCapabilities;
  onChoose: (assumptions: Assumptions) => Promise<void>;
}) {
  const [manualCents, setManualCents] = useState<number | null>(null);
  const [saving, setSaving] = useState<MetricsBase>();
  const [error, setError] = useState<string>();

  if (!capabilities.canManageAssumptions) {
    return (
      <EmptyCard
        icon={Info}
        title="Os indicadores ainda não têm base de cálculo"
        description="Quem administra o investimento precisa escolher se o investimento inicial é a soma dos lançamentos, a soma dos aportes ou um valor informado."
      />
    );
  }

  const amountOf = (kind: MetricsBase): number | null =>
    kind === 'ENTRIES'
      ? view.metrics.totals.entriesCents
      : kind === 'CONTRIBUTIONS'
        ? view.metrics.totals.contributionsCents
        : manualCents;

  async function choose(kind: MetricsBase) {
    setSaving(kind);
    setError(undefined);
    try {
      await onChoose({
        ...view.assumptions,
        metricsBase: kind,
        manualBaseCents: kind === 'MANUAL' ? manualCents : null,
      });
    } catch (failure) {
      setError(getApiError(failure));
    } finally {
      setSaving(undefined);
    }
  }

  return (
    <div className="rounded-2xl border border-surface-border bg-surface-card p-5 shadow-soft sm:p-6">
      <ContentCardHeader
        title="De onde sai o investimento inicial?"
        icon={<CardIconLandmark className="size-4" />}
      />
      <p className="mt-1.5 text-sm text-content-muted">
        Todos os indicadores partem desse valor. Não há resposta única — cada empresa mede o próprio
        investimento de um jeito, e a escolha fica registrada nas premissas.
      </p>
      <ul className="mt-5 space-y-3">
        {BASE_CHOICES.map((choice) => {
          const amount = amountOf(choice.value);
          const blocked = choice.value === 'MANUAL' && (manualCents === null || manualCents <= 0);
          return (
            <li
              key={choice.value}
              className="rounded-xl border border-surface-border bg-surface-elevated/50 p-4"
            >
              <div className="flex flex-wrap items-baseline justify-between gap-2">
                <strong className="text-sm font-semibold">{choice.title}</strong>
                <span className="text-sm font-semibold tabular-nums text-brand-400">
                  {amount === null ? '—' : formatCurrency(amount)}
                </span>
              </div>
              <p className="mt-1 text-xs leading-5 text-content-muted">{choice.explanation}</p>
              {choice.value === 'MANUAL' ? (
                <div className="mt-3 max-w-xs">
                  <CurrencyInput
                    label="Quanto foi investido"
                    value={manualCents}
                    onValueChange={setManualCents}
                  />
                </div>
              ) : null}
              <Button
                variant="outline"
                className="mt-3"
                disabled={blocked || saving !== undefined}
                onClick={() => void choose(choice.value)}
              >
                {saving === choice.value ? 'Salvando…' : 'Usar esta base'}
              </Button>
            </li>
          );
        })}
      </ul>
      {error ? <RequestAlert message={error} /> : null}
    </div>
  );
}

function IndicatorGroup({
  title,
  description,
  group,
  indicators,
}: {
  title: string;
  description: string;
  group: IndicatorInfo[];
  indicators: Record<string, Metric>;
}) {
  return (
    <div className="mb-8">
      <div className="mb-3">
        <h2 className="text-sm font-semibold">{title}</h2>
        <p className="text-xs text-content-faint">{description}</p>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {group.map((info) => (
          <IndicatorCard key={info.key} info={info} metric={indicators[info.key]} />
        ))}
      </div>
    </div>
  );
}

function IndicatorCard({ info, metric }: { info: IndicatorInfo; metric: Metric | undefined }) {
  const missing = metric?.missing ?? [];
  return (
    <div className="rounded-2xl border border-surface-border bg-surface-card p-4 shadow-soft">
      <div className="flex items-start justify-between gap-2">
        <strong className="text-xs font-semibold uppercase tracking-wide text-content/70">
          {info.title}
        </strong>
        <Tooltip content={info.explanation}>
          <button
            type="button"
            aria-label={`O que é ${info.title}`}
            className="shrink-0 rounded-full p-0.5 text-content-faint transition hover:text-brand-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/50"
          >
            <Info className="size-4" aria-hidden="true" />
          </button>
        </Tooltip>
      </div>
      <p className="mt-2 text-2xl font-bold tabular-nums tracking-[-.03em]">
        {formatMetric(metric)}
      </p>
      {missing.length ? (
        <p className="mt-1.5 text-xs leading-5 text-content-faint">
          Falta informar: {missing.join(', ')}.
        </p>
      ) : null}
    </div>
  );
}

/// O diálogo só existe enquanto está aberto.
///
/// É o que garante que reabrir mostre o que está salvo, e não o que alguém digitou e abandonou da
/// última vez: o estado nasce das premissas a cada montagem, sem efeito sincronizando um espelho.
function AssumptionsModal({
  onOpenChange,
  assumptions,
  onSave,
}: {
  onOpenChange: (open: boolean) => void;
  assumptions: Assumptions;
  onSave: (assumptions: Assumptions) => Promise<void>;
}) {
  const [form, setForm] = useState<Assumptions>(assumptions);
  const [rate, setRate] = useState(() => formatScaled(assumptions.discountRateAnnualBp, 100));
  const [wacc, setWacc] = useState(() => formatScaled(assumptions.waccBp, 100));
  const [margin, setMargin] = useState(() => formatScaled(assumptions.contributionMarginBp, 100));
  const [multiple, setMultiple] = useState(() =>
    formatScaled(assumptions.ebitdaMultipleHundredths, 100),
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();

  const set = (patch: Partial<Assumptions>) => setForm((current) => ({ ...current, ...patch }));

  async function submit() {
    setSaving(true);
    setError(undefined);
    try {
      await onSave({
        ...form,
        discountRateAnnualBp: parseScaled(rate, 100) ?? 0,
        waccBp: parseScaled(wacc, 100),
        contributionMarginBp: parseScaled(margin, 100),
        ebitdaMultipleHundredths: parseScaled(multiple, 100),
        manualBaseCents: form.metricsBase === 'MANUAL' ? form.manualBaseCents : null,
      });
    } catch (failure) {
      // O diálogo continua aberto com o que foi digitado: quem errou um número corrige aquele
      // número, em vez de preencher tudo de novo.
      setError(getApiError(failure));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open
      onOpenChange={onOpenChange}
      title="Premissas dos indicadores"
      description="Taxa de desconto e horizonte vêm preenchidos porque valem para qualquer negócio; são pontos de partida do mercado e devem ser trocados pelos números desta empresa. O resto começa em branco de propósito: um campo vazio faz o indicador dizer o que falta, enquanto um número chutado faz ele mentir com confiança."
    >
      <div className="space-y-6 overflow-y-auto px-5 py-5 sm:px-6">
        <fieldset className="space-y-3">
          <legend className="text-xs font-semibold uppercase tracking-wide text-content/70">
            Base do cálculo
          </legend>
          <Select
            label="Investimento inicial"
            value={form.metricsBase ?? ''}
            options={BASE_CHOICES.map((choice) => ({
              value: choice.value,
              label: choice.title,
            }))}
            onValueChange={(value) => set({ metricsBase: value as MetricsBase })}
          />
          {form.metricsBase === 'MANUAL' ? (
            <CurrencyInput
              label="Quanto foi investido"
              value={form.manualBaseCents}
              onValueChange={(cents) => set({ manualBaseCents: cents })}
            />
          ) : null}
        </fieldset>

        <fieldset className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-content/70">
            Fluxo projetado
          </legend>
          <Input
            label="Taxa de desconto ao ano (%)"
            inputMode="decimal"
            value={rate}
            onChange={(event) => setRate(event.target.value)}
            hint="Quanto o dinheiro renderia na melhor alternativa."
          />
          <Input
            label="Horizonte (meses)"
            inputMode="numeric"
            value={String(form.horizonMonths)}
            onChange={(event) =>
              set({ horizonMonths: Number(event.target.value.replace(/\D/gu, '')) || 1 })
            }
          />
          <CurrencyInput
            label="Receita mensal"
            value={form.monthlyRevenueCents}
            onValueChange={(cents) => set({ monthlyRevenueCents: cents })}
          />
          <CurrencyInput
            label="Despesa mensal"
            value={form.monthlyExpenseCents}
            onValueChange={(cents) => set({ monthlyExpenseCents: cents })}
          />
        </fieldset>

        <fieldset className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-content/70">
            Resultado e capital
          </legend>
          <CurrencyInput
            label="Lucro líquido anual"
            value={form.annualNetProfitCents}
            onValueChange={(cents) => set({ annualNetProfitCents: cents })}
          />
          <CurrencyInput
            label="Capital investido"
            value={form.investedCapitalCents}
            onValueChange={(cents) => set({ investedCapitalCents: cents })}
          />
          <CurrencyInput
            label="Patrimônio líquido"
            value={form.equityCents}
            onValueChange={(cents) => set({ equityCents: cents })}
          />
          <CurrencyInput
            label="Ativo total"
            value={form.totalAssetsCents}
            onValueChange={(cents) => set({ totalAssetsCents: cents })}
          />
          <Input
            label="WACC ao ano (%)"
            inputMode="decimal"
            value={wacc}
            onChange={(event) => setWacc(event.target.value)}
          />
          <CurrencyInput
            label="EBITDA anual"
            value={form.annualEbitdaCents}
            onValueChange={(cents) => set({ annualEbitdaCents: cents })}
          />
          <Input
            label="Múltiplo de EBITDA"
            inputMode="decimal"
            value={multiple}
            onChange={(event) => setMultiple(event.target.value)}
            hint="Quantas vezes o EBITDA anual vale o negócio no seu setor."
          />
        </fieldset>

        <fieldset className="grid grid-cols-1 gap-3 sm:grid-cols-2">
          <legend className="mb-1 text-xs font-semibold uppercase tracking-wide text-content/70">
            Clientes
          </legend>
          <CurrencyInput
            label="Gasto com marketing"
            value={form.marketingSpendCents}
            onValueChange={(cents) => set({ marketingSpendCents: cents })}
          />
          <Input
            label="Clientes novos no período"
            inputMode="numeric"
            value={form.newCustomers === null ? '' : String(form.newCustomers)}
            onChange={(event) => set({ newCustomers: countOf(event.target.value) })}
          />
          <CurrencyInput
            label="Ticket médio"
            value={form.averageTicketCents}
            onValueChange={(cents) => set({ averageTicketCents: cents })}
          />
          <Input
            label="Margem de contribuição (%)"
            inputMode="decimal"
            value={margin}
            onChange={(event) => setMargin(event.target.value)}
          />
          <Input
            label="Vida do cliente (meses)"
            inputMode="numeric"
            value={form.customerLifetimeMonths === null ? '' : String(form.customerLifetimeMonths)}
            onChange={(event) => set({ customerLifetimeMonths: countOf(event.target.value) })}
          />
        </fieldset>
      </div>

      <div className="shrink-0 border-t border-surface-border px-5 py-4 sm:px-6">
        {error ? (
          <p
            role="alert"
            className="mb-3 rounded-xl border border-danger/35 bg-danger/10 px-4 py-3 text-sm text-danger"
          >
            {error}
          </p>
        ) : null}
        <div className="flex flex-col-reverse gap-2 sm:flex-row sm:justify-end">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button onClick={() => void submit()} disabled={saving}>
            {saving ? 'Salvando…' : 'Salvar premissas'}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

function countOf(text: string): number | null {
  const digits = text.replace(/\D/gu, '');
  return digits === '' ? null : Number(digits);
}
